import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LoadScript, Autocomplete } from "@react-google-maps/api";
import axios from "axios";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import { HiOutlinePlusCircle, HiOutlineTrash, HiOutlineArrowPath, HiOutlineXCircle } from "react-icons/hi2";

const libraries = ["places"];

const EditarSolicitudSinAsignar = () => {
  const navigate = useNavigate();
  const { id, method } = useParams();
  const [reserva, setReserva] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const autocompleteRef = useRef(null);

  const [formData, setFormData] = useState({
    status: "", empresa: "", contacto: "", equipo: "", domicilio: "",
    localidad: "", ciudad: "", telefono: "", actividades: [], itinerario: [],
  });

  const [itinerario, setItinerario] = useState([]);

  useEffect(() => { fetchReserva(); }, [id]);

  const fetchReserva = async () => {
    try {
      const response = await axios.get(`https://desarrollotecnologicoar.com/api5/reserva_id/${id}`);
      const data = response.data;
      const itinerarioFormatted = data.itinerario.map((day) => ({
        id: day.id, fecha: new Date(day.fecha.split("T")[0] + "T12:00:00Z"),
        entrada: day.entrada, salida: day.salida, nuevo: false, modifico: false, id_reporte: day.id_reporte,
      }));
      setReserva(data);
      setFormData({
        status: data.status, empresa: data.empresa, contacto: data.contacto, equipo: data.equipo,
        domicilio: data.domicilio, localidad: data.localidad, ciudad: data.ciudad,
        telefono: data.telefono, actividades: data.actividades, itinerario: itinerarioFormatted,
      });
      setItinerario(itinerarioFormatted);
    } catch (err) { setError("Error al obtener la reserva."); console.error(err); }
    finally { setLoading(false); }
  };

  const tieneReporteAsociado = (day) => {
    if (!day || day.nuevo) return false;
    if (day.id_reporte && day.id_reporte !== null) return true;
    if (!reserva?.reportes_servicio || reserva.reportes_servicio.length === 0) return false;
    return reserva.reportes_servicio.some((r) => r.itinerario_id === day.id);
  };

  const handleItinerarioChange = (index, field, value) => {
    setItinerario((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      updated[index].modifico = true;
      return updated;
    });
  };

  const handleRemoveItinerario = async (index, idBuscado) => {
    if (!window.confirm("¿Estas seguro de que deseas eliminar este dia del itinerario?")) return;
    try {
      await axios.delete(`https://desarrollotecnologicoar.com/api5/itinerario_ticket/${idBuscado}`);
      setItinerario((prev) => prev.filter((_, i) => i !== index));
      alert("Dia eliminado correctamente.");
    } catch (error) { console.error("Error al eliminar itinerario:", error); alert("Error al eliminar el dia del itinerario."); }
  };

  const handleAddItinerario = () => {
    setItinerario((prev) => [...prev, { fecha: new Date(), entrada: "", salida: "", nuevo: true, modifico: false }]);
  };

  const handleAgregarNuevo = async (index) => {
    const resultado = itinerario[index];
    if (!resultado) { alert("No se encontro el itinerario."); return; }
    const nuevoItinerario = { reserva_id: id, itinerario: [{ fecha: resultado.fecha, entrada: resultado.entrada, salida: resultado.salida }] };
    try {
      const response = await axios.post("https://desarrollotecnologicoar.com/api5/itinerario_ticket_extension", nuevoItinerario, { headers: { "Content-Type": "application/json" } });
      const nuevoId = response.data.data[0].id;
      setItinerario((prev) => prev.map((day, i) => i === index ? { ...day, id: nuevoId, nuevo: false, modifico: false } : day));
      alert("Itinerario agregado correctamente.");
    } catch (error) { console.error("Error al agregar itinerario:", error.response?.data || error.message); alert("Error al agregar el itinerario."); }
  };

  const handleSubmit = async () => {
    if (itinerario.some((d) => d.nuevo)) {
      alert("Tienes dias nuevos sin guardar. Usa el botón 'Subir' en cada día nuevo antes de continuar.");
      return;
    }
    if (itinerario.some((d) => d.modifico)) {
      alert("Tienes dias con cambios sin guardar. Usa el botón 'Actualizar' en cada día modificado antes de continuar.");
      return;
    }
    try {
      const updatedData = {
        ticketId: reserva.ticketid, empresa: formData.empresa, contacto: formData.contacto,
        equipo: formData.equipo, numero_serie: reserva.numero_serie || "", ticket: reserva.ticket || "",
        fecha_reserva: reserva.fecha_reserva || "", hora_reserva: reserva.hora_reserva || "",
        status: formData.status || "Pendiente", correo_cliente: reserva.correo_cliente || "",
        correo_tecnico: reserva.correo_tecnico || "", domicilio: formData.domicilio,
        localidad: formData.localidad, ciudad: formData.ciudad, telefono: formData.telefono,
      };
      await axios.put(`https://desarrollotecnologicoar.com/api5/reservas/${id}`, updatedData, { headers: { "Content-Type": "application/json" } });
      alert("Reserva actualizada correctamente.");
      navigate(method == "pendientes" ? "/pendientes" : "/actividad");
    } catch (error) { console.error("Error al actualizar la reserva:", error.response?.data || error.message); alert("Error al actualizar la reserva."); }
  };

  const handleItinerarioById = async (idBuscado) => {
    const resultado = itinerario.find((item) => item.id === idBuscado);
    const datosActualizados = { reserva_id: id, fecha: resultado.fecha, entrada: resultado.entrada, salida: resultado.salida };
    try {
      await axios.put(`https://desarrollotecnologicoar.com/api5/itinerario_ticket/${idBuscado}`, datosActualizados, { headers: { "Content-Type": "application/json" } });
      setItinerario((prev) => prev.map((day) => day.id === idBuscado ? { ...day, modifico: false } : day));
      alert("Fecha actualizada correctamente.");
    } catch (error) { console.error("Error al actualizar itinerario:", error); alert("Error al actualizar el itinerario."); }
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.address_components) {
        const cityComponent = place.address_components.find((comp) => comp.types.includes("locality") || comp.types.includes("administrative_area_level_1"));
        setFormData({ ...formData, domicilio: place.formatted_address, ciudad: cityComponent ? cityComponent.long_name : "" });
      }
    }
  };

  const handleCancelar = () => { navigate(method == "pendientes" ? "/pendientes" : "/actividad"); };

  const handleCancelarReserva = async () => {
    const diasConReporte = itinerario.filter((day) => !day.nuevo && tieneReporteAsociado(day));
    const diasSinReporte = itinerario.filter((day) => !day.nuevo && !tieneReporteAsociado(day));
    const mensaje = diasConReporte.length > 0
      ? `Se eliminaran ${diasSinReporte.length} dia(s) del itinerario. ${diasConReporte.length} dia(s) con reporte asociado se conservaran. ¿Deseas continuar?`
      : "¿Estas seguro de que deseas cancelar esta reserva? Se eliminaran todos los dias del itinerario y el estatus cambiara a Cancelado.";
    if (!window.confirm(mensaje)) return;
    try {
      for (const day of diasSinReporte) {
        await axios.delete(`https://desarrollotecnologicoar.com/api5/itinerario_ticket/${day.id}`);
      }
      const updatedData = {
        ticketId: reserva.ticketid, empresa: formData.empresa, contacto: formData.contacto,
        equipo: formData.equipo, numero_serie: reserva.numero_serie || "", ticket: reserva.ticket || "",
        fecha_reserva: reserva.fecha_reserva || "", hora_reserva: reserva.hora_reserva || "",
        status: "Cancelado", correo_cliente: reserva.correo_cliente || "",
        correo_tecnico: reserva.correo_tecnico || "", domicilio: formData.domicilio,
        localidad: formData.localidad, ciudad: formData.ciudad, telefono: formData.telefono,
      };
      await axios.put(`https://desarrollotecnologicoar.com/api5/reservas/${id}`, updatedData, { headers: { "Content-Type": "application/json" } });
      alert("Reserva cancelada correctamente.");
      navigate(method == "pendientes" ? "/pendientes" : "/actividad");
    } catch (error) {
      console.error("Error al cancelar la reserva:", error);
      alert("Error al cancelar la reserva.");
    }
  };

  if (loading) return <div className="page-container"><div className="flex items-center justify-center h-64"><svg className="w-8 h-8 animate-spin text-slate-400" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg></div></div>;
  if (error) return <div className="page-container"><div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">{error}</div></div>;

  return (
    <LoadScript googleMapsApiKey="AIzaSyDHkpz3qbH2j6KuFgytJdSNEJp3Pp7XaL0" libraries={libraries}>
      <div className="page-container">
        <div className="flex items-center justify-between mb-6">
          <h1 className="page-title">Editar Reserva - Ticket #{reserva?.ticket}</h1>
          <button onClick={handleCancelarReserva} className="text-sm py-2 px-4 inline-flex items-center gap-1.5 bg-red-600 text-white rounded-btn font-semibold hover:bg-red-700 transition-colors">
            <HiOutlineXCircle className="w-4 h-4" /> Cancelar Reserva
          </button>
        </div>

        {/* Form Card */}
        <div className="card p-5 space-y-4 mb-6">
          <div>
            <label className="label">Estatus</label>
            <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} disabled={method === "actividad" || method === "compartidas"} className="input">
              <option value="Pendiente">Pendiente</option>
              <option value="Activo">Activo</option>
              <option value="Finalizado">Finalizado</option>
              <option value="Cancelado">Cancelado</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="label">Empresa</label><input type="text" value={formData.empresa} onChange={(e) => setFormData({ ...formData, empresa: e.target.value })} className="input" /></div>
            <div><label className="label">Contacto</label><input type="text" value={formData.contacto} onChange={(e) => setFormData({ ...formData, contacto: e.target.value })} className="input" /></div>
            <div><label className="label">Equipo</label><input type="text" value={formData.equipo} onChange={(e) => setFormData({ ...formData, equipo: e.target.value })} className="input" /></div>
            <div><label className="label">Ciudad</label><input type="text" value={formData.ciudad} onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })} className="input" /></div>
          </div>

          <div>
            <label className="label">Domicilio</label>
            <Autocomplete onLoad={(a) => (autocompleteRef.current = a)} onPlaceChanged={handlePlaceChanged}>
              <input type="text" value={formData.domicilio} onChange={(e) => setFormData({ ...formData, domicilio: e.target.value })} className="input" autoComplete="off" />
            </Autocomplete>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Localidad</label>
              <select value={formData.localidad} onChange={(e) => setFormData({ ...formData, localidad: e.target.value })} className="input">
                <option value="Local">Local</option>
                <option value="Foraneo">Foraneo</option>
              </select>
            </div>
            <div><label className="label">Telefono</label><input type="tel" value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} className="input" /></div>
          </div>
        </div>

        {/* Itinerario */}
        <h2 className="section-title mb-4">Itinerario</h2>
        <div className="space-y-3 mb-6">
          {itinerario.map((day, index) => {
            const tieneReporte = tieneReporteAsociado(day);
            return (
              <div key={index} className={`card p-4 ${tieneReporte ? "opacity-60" : ""}`}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                  <div>
                    <label className="label">Fecha</label>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                      <DatePicker
                        value={day.fecha}
                        format="dd/MM/yyyy"
                        onChange={(v) => handleItinerarioChange(index, "fecha", v)}
                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                      />
                    </LocalizationProvider>
                  </div>
                  <div><label className="label">Entrada</label><input type="time" value={day.entrada} onChange={(e) => handleItinerarioChange(index, "entrada", e.target.value)} className="input" /></div>
                  <div><label className="label">Salida</label><input type="time" value={day.salida} onChange={(e) => handleItinerarioChange(index, "salida", e.target.value)} className="input" /></div>
                </div>

                <div className="flex items-center gap-2 mt-3">
                  {day.nuevo ? (
                    <button onClick={() => handleAgregarNuevo(index)} className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1">
                      <HiOutlinePlusCircle className="w-4 h-4" /> Subir
                    </button>
                  ) : (
                    <>
                      {day.modifico && (
                        <button onClick={() => { if (tieneReporte) { alert("No puedes actualizar un itinerario con reporte generado."); return; } handleItinerarioById(day.id); }} disabled={tieneReporte} className="text-xs py-1.5 px-3 inline-flex items-center gap-1 rounded-btn font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50">
                          <HiOutlineArrowPath className="w-4 h-4" /> Actualizar
                        </button>
                      )}
                      <button onClick={() => { if (tieneReporte) { alert("No puedes eliminar un itinerario con reporte generado."); return; } handleRemoveItinerario(index, day.id); }} disabled={tieneReporte} className="text-xs py-1.5 px-3 inline-flex items-center gap-1 rounded-btn font-medium bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50">
                        <HiOutlineTrash className="w-4 h-4" /> Eliminar
                      </button>
                      {tieneReporte && <span className="text-xs text-slate-500 italic">Dia bloqueado (reporte #{day.id_reporte || "asociado"})</span>}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button onClick={handleAddItinerario} className="btn-ghost text-sm">+ Agregar Dia</button>
          <button onClick={handleSubmit} className="btn-primary text-sm">Guardar Cambios</button>
          <button onClick={handleCancelar} className="btn-secondary text-sm">Cancelar</button>
        </div>
      </div>
    </LoadScript>
  );
};

export default EditarSolicitudSinAsignar;
